import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Column,
  Columns,
  Divider,
  Hidden,
  Stack,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import PropertyType from "../FormComponents/PropertyType";
import AddressField from "../FormComponents/AddressField";
import PropertyAccess from "../FormComponents/PropertyAccess";
import { ServiceTypesWrapper } from "../FormComponents/ServiceTypes";
import { IFormFunctionProps, IFormProps } from "../FormAccordionContainer";
import { Field, FormikErrors, useFormikContext, yupToFormErrors } from "formik";
import FormContext, {
  ElectricityService,
  GasServices,
  InternetService,
} from "src/context/FormContext";
import { homeUserPremiseDraftValidationSchema } from "../FormComponents/Validations";
import {
  formDataToFormikMapper,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";
import {
  EnergyType,
  ErrorTypeEnum,
  FormValues,
  GasOptionEnum,
  Service,
} from "../types";
import {
  actAddressEmptyFields,
  nswAddressEmptyFields,
  showUpdatePremiseAlert,
} from "../helper";
import { useUpdateSidebarStatus } from "../../Hooks/useSidebarStatus";
import { useAppContext } from "../../../context/AppContext";
import ClaimDialog from "../Claim/ClaimDialog";
import UpdatePremiseAlert from "../UpdatePremiseAlert";
import ReadOnlyPropertyAccessInPremise from "../ReadOnlyPropertyAccessInPremise";
import { RequestStatusEnum, State } from "src/components/Premises/types";
import {
  getFormattedAddress,
  isAllowedForQldElec,
} from "../../Premises/helpers";
import {
  getElectricityServiceContext,
  getIsHomeUserAccountReadOnly,
  getGasServicesContext,
  getInternetServiceContext,
} from "../helpers";
import ReadOnlyFieldWrapper from "../ReadOnlyFieldWrapper";
import HomeUserClaimDialog from "../Claim/HomeUserClaimDialog";
import HomeUserFuelInPremise from "./HomeUserFuelInPremise";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import PermissionsControlledWrapper from "src/components/Permissions/PermissionsControlledWrapper";
import { PermissionEnum } from "src/components/Permissions/types";
import { InternetRequestStatusEnum } from "../ServiceTypeStatusAlert";
import KnockdownRebuildBlock from "../KnockdownRebuild";

interface IPremiseBlockProps extends IFormProps, IFormFunctionProps {
  savingBlock: boolean;
  servicesSelected: EnergyType[];
  state: string;
  postcode: string;
  errors: FormikErrors<any>;
  validateForm: () => Promise<FormikErrors<any>>;
  setPremiseId: React.Dispatch<React.SetStateAction<string | null>>;
  premiseId: string | null;
  setFormLoadValues: React.Dispatch<React.SetStateAction<FormValues>>;
  gasOptions: GasOptionEnum[];
  rawFormLoadData: any;
  homeUser: boolean;
}

const HomeUserPremiseBlock = (props: IPremiseBlockProps) => {
  const {
    setFieldValue,
    submitForm,
    validateForm,
    submitClicked,
    setSubmitClicked,
    savingBlock,
    servicesSelected,
    state,
    postcode,
    errors,
    premiseId,
    setPremiseId,
    setFormLoadValues,
    gasOptions,
    rawFormLoadData,
    homeUser,
  } = props;
  const { values, setErrors } = useFormikContext<FormValues>();
  const { setDisplayingSnackbar, setDisplayingAlertBanner } =
    React.useContext(FormContext);
  const {
    context: { user, featureFlags },
  } = useAppContext();
  const [claimDialogOpen, setClaimDialogOpen] = useState<boolean>(false);
  const accountPayers = React.useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "premiseBlock",
    !!premiseId
  );

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      console.log("errors", errors);
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  // useEffect(() => {
  //   if(homeUser && !accountPayers.includes(AccountPayer.SOMEONE_ELSE)){
  //     setFieldValue("accountPrimaryEmail", user?.email)
  //     setFieldValue("accountPrimaryFirstName", user?.first_name)
  //     setFieldValue("accountPrimaryLastName", user?.last_name)
  //   }
  // }, [homeUser, accountPayers])

  const savePremiseBlock = async (
    formValues: FormValues,
    services?: {
      electricityService: ElectricityService;
      gasServices: GasServices;
      internetService?: InternetService;
    }
  ) => {
    try {
      const { data } = await apiClient.post(
        "/draft/premise",
        premiseFormFieldMapper(
          formValues,
          services,
          undefined,
          true,
          undefined,
          undefined,
          true
        )
      );

      setPremiseId(data.premise.id);
      const mappedFormikValues = formDataToFormikMapper(data);
      setFormLoadValues({
        ...mappedFormikValues,
        siteSupervisors: formValues.siteSupervisors,
        licensedPlumbers: formValues.licensedPlumbers,
      });

      const electricityService = data.services.find(
        (s: Service) => s.service_type === "ELECTRICITY"
      );
      const internetService = data.services.find(
        (s: Service) => s.service_type === "INTERNET"
      );

      setFormState((prevState) => ({
        ...prevState,
        state: formValues.premiseAddress.state,
        siteDetails: data.premise.site_details,
        address: getFormattedAddress(data.premise),
        msaNumber: formValues.requestingCompany?.msaNumber ?? "",
        postcode: formValues.premiseAddress.postcode,
        electricityService: getElectricityServiceContext(electricityService),
        gasServices: getGasServicesContext(data.services),
        internetService: getInternetServiceContext(internetService),
        isAccountReadOnly: getIsHomeUserAccountReadOnly(data.services),
      }));

      setDisplayingSnackbar({
        message: `Premise ${premiseId ? "updated" : "created"}`,
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (e) {
      setDisplayingSnackbar({
        message: `Couldn't ${premiseId ? "update" : "create"} premise`,
        tone: "caution",
      });
      console.error("Failed to save premise:", e);
    }
  };

  useEffect(() => {
    if (submitClicked) {
      submitForm();
    }
    setSubmitClicked(false);
  }, [submitClicked, submitForm, setSubmitClicked]);

  //cleanup for property type pathway ui selections
  useEffect(() => {
    if (!premiseId) {
      if (values.premiseAddress.state === State.ACT) {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
          ...actAddressEmptyFields,
        });
      } else if (values.premiseAddress.state === State.NSW) {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
          ...nswAddressEmptyFields,
        });
      } else {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
        });
      }
    }
  }, [values.propertyType]);

  const {
    setFormState,
    formState: { electricityService, gasServices, internetService },
  } = React.useContext(FormContext);

  const isAnyServiceReadOnly =
    electricityService?.isReadOnly ||
    internetService?.isReadOnly ||
    (!!internetService &&
      (internetService?.requestStatus === RequestStatusEnum.IN_PROGRESS ||
        internetService?.requestStatus === RequestStatusEnum.CANCELLED)) ||
    gasOptions.map((option) => gasServices?.[option]?.isReadOnly).some(Boolean);

  const isOnlyInternetValid =
    values.selectedConnections.includes("Electricity") ||
    values.selectedConnections.includes("Gas") ||
    (values.selectedConnections.includes("Internet") &&
      values.selectedConnections.length === 1 &&
      (featureFlags.enablePreinstallAddrNotFound ||
        values.internetRequestStatus === InternetRequestStatusEnum.CAN_RETAIL ||
        internetService?.connectionType === "CAN_RETAIL") &&
      (values.internetRequestStatus === InternetRequestStatusEnum.CAN_RETAIL ||
        (values.internetRequestStatus ===
          InternetRequestStatusEnum.PRE_INSTALL_NBN &&
          values.isShowInternetSectionInPremise) ||
        (values.internetRequestStatus ===
          InternetRequestStatusEnum.ADDRESS_NOT_FOUND &&
          values.isShowInternetSectionInPremise) ||
        (internetService?.connectionType &&
          (internetService?.connectionType === "CAN_RETAIL" ||
            (internetService?.connectionType === "ADDRESS_NOT_FOUND" &&
              values.isShowInternetSectionInPremise) ||
            (internetService?.connectionType === "PRE_INSTALL_NBN" &&
              values.isShowInternetSectionInPremise)))));

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* Premise details */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ReadOnlyFieldWrapper value={true}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Requestor Name
                    </Text>
                    <Text>{`${
                      premiseId && rawFormLoadData?.services?.[0].requestor
                        ? rawFormLoadData?.services?.[0].requestor
                        : user?.first_name
                    } ${
                      premiseId && rawFormLoadData?.services?.[0].requestor
                        ? ""
                        : user?.last_name
                    }`}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>
              <ReadOnlyFieldWrapper value={true}>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Requestor Email
                    </Text>
                    <Text>
                      {premiseId &&
                      rawFormLoadData?.services?.[0].requestor_email
                        ? rawFormLoadData?.services?.[0].requestor_email
                        : user?.email}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
              </ReadOnlyFieldWrapper>

              <Field
                id="property-type"
                name="propertyType"
                component={PropertyType}
                isReadOnly={!!premiseId}
              />
              {/* Address */}
              <AddressField
                setFieldValue={setFieldValue}
                submitForm={submitForm}
                validateForm={validateForm}
                isReadOnly={!!premiseId}
              />
              {/* End Address */}
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>

        {/* Safe access */}
        <FormFieldLayout text="Clear access to the property and meter site is essential; otherwise, rescheduling and fees may occur, impacting installation timelines.">
          {electricityService?.isReadOnly ? (
            <ReadOnlyPropertyAccessInPremise />
          ) : (
            <PropertyAccess />
          )}
        </FormFieldLayout>

        {/* End Safe access */}

        {/* Services sections */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ServiceTypesWrapper
                error={errors["selectedConnections"]}
                isEditing={!!premiseId}
              />
            </Stack>
          </Column>
          <Column width="1/3">
            <Box display="flex" alignItems="stretch" height="full">
              <>
                <Hidden below="lg">
                  {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="medium"
                    height="full"
                  />
                  {/* End Faux vertical <Divider> */}
                </Hidden>
                {!electricityService?.isReadOnly &&
                state === "VIC" &&
                values.selectedConnections.includes("Electricity") ? (
                  <Box
                    alignSelf="center"
                    paddingBottom={{
                      xs: "large",
                      lg: "none",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      For Powercor, Citipower and United Energy, you will need
                      an DB application ID and then{" "}
                      <TextLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setClaimDialogOpen(!claimDialogOpen);
                        }}
                      >
                        <Strong>claim it here</Strong>
                      </TextLink>
                    </Text>
                  </Box>
                ) : null}
                {!electricityService?.isReadOnly &&
                state === "QLD" &&
                values.selectedConnections.includes("Electricity") &&
                !isAllowedForQldElec(postcode) ? (
                  <Box
                    alignSelf="center"
                    paddingBottom={{
                      xs: "large",
                      lg: "none",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      For an electricity new connection in QLD, you will need to
                      have the EWR number from Energex and then{" "}
                      <TextLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setClaimDialogOpen(!claimDialogOpen);
                        }}
                      >
                        <Strong>claim it here</Strong>
                      </TextLink>
                    </Text>
                  </Box>
                ) : null}
              </>
            </Box>
          </Column>
        </Columns>
        {/* End Services sections */}

        {/* End Premise details */}

        {/* Knockdown rebuild section appears here */}
        {featureFlags.enablePreinstallAddrNotFound &&
          values.selectedConnections.includes("Internet") &&
          (values.internetRequestStatus === "PRE_INSTALL_NBN" ||
            values.internetRequestStatus === "ADDRESS_NOT_FOUND") && (
            <KnockdownRebuildBlock />
          )}

        {/* Proof of address section appears here */}

        {values.selectedConnections.length > 0 &&
          values.premiseAddress.state &&
          isOnlyInternetValid && (
            <HomeUserFuelInPremise isReadOnly={isAnyServiceReadOnly} />
          )}
      </Stack>
      {!(
        !electricityService?.isReadOnly &&
        state === "QLD" &&
        values.selectedConnections.includes("Electricity") &&
        !isAllowedForQldElec(postcode)
      ) && (
        <Box paddingBottom={["small", "medium"]}>
          <Stack space="xlarge">
            {showUpdatePremiseAlert(
              accountPayers,
              Boolean(values.accountId),
              !!premiseId
            ) && <UpdatePremiseAlert isPremise />}
            <PermissionsControlledWrapper
              requiredPermission={PermissionEnum.CREATE_REQUEST}
            >
              {savingBlock ? (
                <Button spinner>Action</Button>
              ) : (
                <Button
                  onClick={() => {
                    setFormState((prevState) => ({
                      ...prevState,
                      validationSchema: homeUserPremiseDraftValidationSchema,
                    }));
                    //Update premise
                    const services = {
                      electricityService: electricityService ?? {},
                      gasServices: gasServices!,
                      internetService: internetService ?? {},
                    };
                    homeUserPremiseDraftValidationSchema
                      .validate(values, {
                        abortEarly: false,
                        context: {
                          gasOptions,
                          servicesSelected,
                        },
                      })
                      .then(() => {
                        savePremiseBlock(
                          values,
                          premiseId ? services : undefined
                        );
                      })
                      .catch((e) => {
                        setErrors(yupToFormErrors(e));
                      });
                  }}
                  disabled={
                    (values.isDisablePremiseButton &&
                      values.selectedConnections.length === 1 &&
                      values.selectedConnections.includes("Internet")) ||
                    (!featureFlags.enablePreinstallAddrNotFound &&
                      values.selectedConnections.includes("Internet") &&
                      (values.internetRequestStatus === "ADDRESS_NOT_FOUND" ||
                        values.internetRequestStatus === "PRE_INSTALL_NBN"))
                  }
                >
                  {premiseId ? "Update Premises" : "Create Premises"}
                </Button>
              )}
            </PermissionsControlledWrapper>
          </Stack>
        </Box>
      )}
      {/* </Accordion> */}
      {/* End Premise section */}
      {homeUser ? (
        <HomeUserClaimDialog
          open={claimDialogOpen}
          setOpen={setClaimDialogOpen}
        />
      ) : (
        <ClaimDialog open={claimDialogOpen} setOpen={setClaimDialogOpen} />
      )}
    </Stack>
  );
};

export default HomeUserPremiseBlock;
